import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Users2, ClipboardCheck, Trophy, Clock, MapPin, AlertTriangle, Loader2,
  CalendarDays, Satellite, ChevronRight, ArrowUpDown, FileDown,
} from "lucide-react";
import { exportTeamDashboardPdf } from "@/lib/export";
import MinutesBreakdownDialog from "@/components/MinutesBreakdownDialog";
import LeagueStandings from "@/components/LeagueStandings";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MIN_A, MIN_B, STAT_COLORS, SCOPES, DEFAULT_SCOPE, scopeToPhase, fmtPct } from "@/lib/minutesStats";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line,
} from "recharts";

const fmtD = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");
const OUT_STYLE = { V: "bg-emerald-600", E: "bg-amber-500", D: "bg-rose-600" };

const ALERT_HELP = {
  medical_caducado: "El reconocimiento médico ha caducado. No deberían competir hasta renovarlo.",
  medical_por_caducar: "El reconocimiento médico caduca pronto. Conviene renovarlo a tiempo.",
  medical_sin_registro: "No hay reconocimiento médico registrado para estos jugadores.",
  lesionados: "Jugadores actualmente marcados como lesionados.",
  sancionados: "Jugadores actualmente sancionados.",
  ausencias_consecutivas: "Jugadores con varias ausencias seguidas a entrenamientos.",
  retrasos_repetidos: "Jugadores con retrasos repetidos a entrenamientos.",
  sin_convocatoria: "Jugadores activos sin convocatoria en los últimos 3 partidos.",
  pocos_minutos: "Jugadores convocados varias veces pero con menos del 25% de minutos del equipo.",
  partidos_sin_acta: "Partidos finalizados pendientes de registrar resultado o acta.",
  partidos_sin_minutos: "Partidos jugados sin minutos registrados de los jugadores.",
  conflictos_rffm: "Partidos con diferencias entre los datos manuales y los de la RFFM.",
  errores_sync: "Errores de sincronización con la RFFM en los últimos 7 días.",
};
const MATCH_ALERTS = new Set(["partidos_sin_acta", "partidos_sin_minutos", "conflictos_rffm"]);
const MEDICAL_ALERTS = new Set(["medical_caducado", "medical_por_caducar", "medical_sin_registro"]);
const STATUS_ALERTS = new Set(["lesionados", "sancionados"]);

function AlertDetailDialog({ alert, onClose, teamId, season, navigate, reload }) {
  const [items, setItems] = useState([]);
  const [dates, setDates] = useState({});
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    setItems(alert ? alert.items : []);
    setDates({});
  }, [alert]);
  if (!alert) return null;

  const isMatch = MATCH_ALERTS.has(alert.key);
  const canMedical = MEDICAL_ALERTS.has(alert.key);
  const canStatus = STATUS_ALERTS.has(alert.key);

  const goItem = (it) => {
    if (alert.key === "errores_sync") { navigate("/rffm-sync"); return; }
    if (isMatch) { navigate(alert.link); return; }
    if (it.id) navigate(`/players/${it.id}/dashboard?team_id=${teamId}&season=${season}`);
  };
  const extra = (it) => {
    if (it.days != null) return `Caduca en ${it.days} día${it.days === 1 ? "" : "s"}`;
    if (it.pct != null) return `${String(it.pct).replace(".", ",")}% min. equipo`;
    if (it.count != null) return `${it.count} vez/veces`;
    if (it.date) return fmtD(it.date);
    return null;
  };
  const resolveMedical = async (it) => {
    const value = dates[it.id];
    if (!value) { toast.error("Indica la fecha de caducidad del reconocimiento"); return; }
    setBusy(it.id);
    try {
      await api.patch(`/players/${it.id}/quick-field`, { field: "medical_expiry", value });
      toast.success(`Reconocimiento actualizado: ${it.name}`);
      setItems((xs) => xs.filter((x) => x.id !== it.id));
      reload && reload();
    } catch (e) { toast.error("No se pudo guardar"); }
    finally { setBusy(null); }
  };
  const resolveStatus = async (it) => {
    setBusy(it.id);
    try {
      await api.patch(`/players/${it.id}/quick-field`, { field: "status", value: "disponible" });
      toast.success(`${it.name} marcado como disponible`);
      setItems((xs) => xs.filter((x) => x.id !== it.id));
      reload && reload();
    } catch (e) { toast.error("No se pudo actualizar"); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open={!!alert} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="alert-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-amber-600" /> {alert.label}
            <span className="text-xs font-normal text-amber-700">({items.length})</span>
          </DialogTitle>
        </DialogHeader>
        {ALERT_HELP[alert.key] && (
          <p className="text-xs text-slate-500 -mt-1">{ALERT_HELP[alert.key]}</p>
        )}
        {items.length === 0 ? (
          <div className="py-6 text-center text-sm text-emerald-600 font-semibold" data-testid="alert-resolved-empty">
            ✓ No queda ningún caso pendiente en esta alerta.
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto divide-y divide-slate-100 rounded-lg border border-slate-200">
            {items.map((it, i) => (
              <div key={it.id || i} className="px-3 py-2" data-testid={`alert-item-${alert.key}-${i}`}>
                <div className="flex items-center justify-between gap-3">
                  <button type="button" onClick={() => goItem(it)}
                    className="text-sm font-semibold text-slate-800 truncate hover:underline text-left flex-1">
                    {it.name}
                  </button>
                  <span className="flex items-center gap-2 shrink-0">
                    {extra(it) && <span className="text-[11px] font-bold text-amber-700 whitespace-nowrap">{extra(it)}</span>}
                    {(it.id || isMatch) && <ChevronRight className="h-4 w-4 text-slate-300" />}
                  </span>
                </div>
                {canMedical && it.id && (
                  <div className="flex items-center gap-2 mt-1.5">
                    <Input type="date" value={dates[it.id] || ""}
                      onChange={(e) => setDates((d) => ({ ...d, [it.id]: e.target.value }))}
                      className="h-7 text-xs w-40" data-testid={`alert-med-date-${i}`} />
                    <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                      disabled={busy === it.id} onClick={() => resolveMedical(it)}
                      data-testid={`alert-med-save-${i}`}>
                      {busy === it.id ? "…" : "Guardar fecha"}
                    </Button>
                  </div>
                )}
                {canStatus && it.id && (
                  <div className="mt-1.5">
                    <Button size="sm" variant="outline" className="h-7 text-xs border-emerald-400 text-emerald-700"
                      disabled={busy === it.id} onClick={() => resolveStatus(it)}
                      data-testid={`alert-status-fix-${i}`}>
                      {busy === it.id ? "…" : "Marcar disponible"}
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end pt-1">
          <Button size="sm" variant="outline" onClick={() => { navigate(alert.link); }} data-testid="alert-detail-open-list">
            Abrir listado completo <ChevronRight className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_STYLE = {
  disponible: "bg-emerald-100 text-emerald-700", lesionado: "bg-rose-100 text-rose-700",
  enfermo: "bg-amber-100 text-amber-700", sancionado: "bg-purple-100 text-purple-700",
  baja: "bg-slate-200 text-slate-600",
};
const MED_STYLE = { caducado: "text-rose-600", por_caducar: "text-orange-500", vigente: "text-emerald-600", sin_registro: "text-slate-400" };

const Mini = ({ label, value, onClick, testId, accent = "" }) => (
  <button type="button" onClick={onClick} disabled={!onClick}
    className={`rounded-xl border border-slate-200 bg-white p-3 text-left transition hover:shadow-md hover:-translate-y-0.5 ${onClick ? "cursor-pointer" : "cursor-default"}`}
    data-testid={testId}>
    <div className={`text-2xl font-black leading-none ${accent}`} style={{ fontFamily: "Nunito" }}>{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
  </button>
);

const Section = ({ title, icon: Icon, children, testId, action }) => (
  <Card className="border-slate-200 shadow-sm" data-testid={testId}>
    <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
      <CardTitle className="text-sm font-black uppercase tracking-wide text-slate-700 flex items-center gap-2">
        {Icon && <Icon className="h-4 w-4 text-purple-600" />} {title}
      </CardTitle>
      {action}
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

export default function TeamDashboard({ teamId, season, canEdit }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("dorsal");
  const [sortDir, setSortDir] = useState(1);
  const [scope, setScope] = useState(DEFAULT_SCOPE);
  const [breakdown, setBreakdown] = useState(null); // {playerId, playerName}
  const [alertDetail, setAlertDetail] = useState(null); // alert object

  const reload = () => {
    const phase = scopeToPhase(scope);
    return api.get("/stats/team-dashboard", { params: { team_id: teamId, season, ...(phase ? { phase } : {}) } })
      .then((r) => setData(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    setLoading(true);
    const phase = scopeToPhase(scope);
    api.get("/stats/team-dashboard", { params: { team_id: teamId, season, ...(phase ? { phase } : {}) } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [teamId, season, scope]);

  if (loading && !data) {
    return <div className="flex justify-center py-16" data-testid="team-dashboard-loading"><Loader2 className="h-8 w-8 animate-spin text-purple-600" /></div>;
  }
  if (!data) return <p className="text-sm text-slate-500 py-8">No se pudo cargar el dashboard del equipo.</p>;

  const { team, squad, attendance, trainings, results, upcoming_matches, sport, alerts } = data;
  const goPlayers = (extra = "") => navigate(`/players?team_id=${teamId}${extra}`);
  const goPlayer = (pid) => navigate(`/players/${pid}/dashboard?team_id=${teamId}&season=${season}`);
  const goTeamStat = (metric) => navigate(`/teams/${teamId}/detalle-stats?metric=${metric}&scope=no_pre&season=${season}`);
  const goPlayerStat = (e, pid, route, qmetric) => {
    e.stopPropagation();
    navigate(`/players/${pid}/${route}?scope=no_pre&season=${season}${qmetric ? `&metric=${qmetric}` : ""}`);
  };
  const sortRows = (key) => {
    if (sortKey === key) setSortDir((d) => -d);
    else { setSortKey(key); setSortDir(1); }
  };
  const rows = [...squad.rows].sort((a, b) => {
    const va = a[sortKey] ?? (typeof a[sortKey] === "string" ? "" : -1);
    const vb = b[sortKey] ?? (typeof b[sortKey] === "string" ? "" : -1);
    return (va < vb ? -1 : va > vb ? 1 : 0) * sortDir;
  });
  const Th = ({ label, k, className = "" }) => (
    <TableHead className={`cursor-pointer select-none whitespace-nowrap ${className}`} onClick={() => sortRows(k)} data-testid={`squad-sort-${k}`}>
      {label}<ArrowUpDown className="h-3 w-3 inline ml-1 text-slate-400" />
    </TableHead>
  );

  return (
    <div className="space-y-5" data-testid="team-dashboard">
      {/* Info básica */}
      <div className="rounded-2xl bg-gradient-to-r from-purple-800 to-purple-600 text-white p-5" data-testid="team-dashboard-header">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-black" style={{ fontFamily: "Nunito" }}>{team.name}</h2>
            <div className="text-purple-100 text-sm mt-0.5">
              {team.category || "Sin categoría"}
              {team.competition ? ` · ${team.competition}` : ""}
              {team.group ? ` · ${team.group}` : ""}
              {" · "}Temporada {season}/{String(season + 1).slice(2)}
            </div>
            <div className="text-purple-200 text-xs mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5">
              {team.coach_name && <span>Entrenador: <b>{team.coach_name}</b></span>}
              {team.assistant_coach_name && <span>2º entrenador: <b>{team.assistant_coach_name}</b></span>}
              <span>{team.players_count} jugadores</span>
            </div>
          </div>
          <div className="text-right text-xs text-purple-100 space-y-1">
            <Button size="sm" variant="secondary" className="h-7 text-xs font-bold bg-white/15 hover:bg-white/25 text-white border border-white/30"
              onClick={() => exportTeamDashboardPdf(team, season)} data-testid="team-dashboard-pdf-btn">
              <FileDown className="h-3.5 w-3.5 mr-1" /> Descargar PDF
            </Button>
            {results.standing && (
              <div className="text-lg font-black text-white" data-testid="team-standing">
                {results.standing.posicion}º de {results.standing_total} · {results.standing.puntos} pts
              </div>
            )}
            <div className="flex items-center gap-1.5 justify-end">
              <Satellite className="h-3.5 w-3.5" />
              {team.rffm_configured
                ? `RFFM sincronizado${team.rffm_last_sync ? ` · ${fmtD(team.rffm_last_sync)}` : ""}`
                : "RFFM sin configurar"}
            </div>
            {team.next_training && <div>Próx. entreno: <b>{fmtD(team.next_training.date)}{team.next_training.time ? ` ${team.next_training.time}` : ""}</b></div>}
            {team.next_match && <div>Próx. partido: <b>{fmtD(team.next_match.date)} vs {team.next_match.rival}</b></div>}
          </div>
        </div>
      </div>

      {/* Alertas */}
      {alerts.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="team-alerts">
          <div className="flex items-center gap-2 text-sm font-black text-amber-900 mb-2">
            <AlertTriangle className="h-4 w-4" /> Alertas ({alerts.length})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {alerts.map((a) => (
              <button key={a.key} type="button" onClick={() => setAlertDetail(a)}
                className="rounded-lg bg-white border border-amber-200 p-2 text-left hover:shadow transition"
                data-testid={`alert-${a.key}`}>
                <div className="text-xs font-bold text-slate-800">{a.label} <span className="text-amber-700">({a.count})</span></div>
                <div className="text-[10px] text-slate-500 truncate">
                  {a.items.map((i) => i.name).join(", ")}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Resumen de plantilla */}
      <Section title="Plantilla" icon={Users2} testId="section-squad"
        action={
          <div className="flex items-center gap-2">
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="h-7 w-36 text-xs" data-testid="squad-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SCOPES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => goPlayers()} data-testid="squad-view-all">Ver plantilla completa <ChevronRight className="h-3 w-3 ml-1" /></Button>
          </div>
        }>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
          <Mini label="Jugadores" value={squad.total} onClick={() => goPlayers()} testId="squad-total" />
          <Mini label="Porteros" value={squad.by_position.porteros} testId="squad-gk" />
          <Mini label="Defensas" value={squad.by_position.defensas} testId="squad-def" />
          <Mini label="Centrocamp." value={squad.by_position.centrocampistas} testId="squad-mid" />
          <Mini label="Delanteros" value={squad.by_position.delanteros} testId="squad-fwd" />
          <Mini label="Disponibles" value={squad.by_status.disponible || 0} accent="text-emerald-600" testId="squad-available" />
          <Mini label="Lesionados" value={squad.by_status.lesionado || 0} accent="text-rose-600" onClick={() => goPlayers("&estado=lesionado")} testId="squad-injured" />
          <Mini label="Enfermos" value={squad.by_status.enfermo || 0} accent="text-amber-600" onClick={() => goPlayers("&estado=enfermo")} testId="squad-sick" />
          <Mini label="Sancionados" value={squad.by_status.sancionado || 0} accent="text-purple-600" onClick={() => goPlayers("&estado=sancionado")} testId="squad-sanctioned" />
          <Mini label="Bajas" value={squad.by_status.baja || 0} onClick={() => goPlayers("&estado=baja")} testId="squad-off" />
          <Mini label="Sin r. médico" value={squad.medical.sin_registro} accent="text-slate-500" onClick={() => goPlayers("&medical=sin_registro")} testId="squad-med-missing" />
          <Mini label="RM caducado/próx." value={squad.medical.caducado + squad.medical.por_caducar} accent="text-rose-600" onClick={() => goPlayers("&medical=caducado")} testId="squad-med-expiring" />
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <Th label="Dorsal" k="dorsal" className="w-14" />
                <Th label="Nombre" k="name" />
                <Th label="Posición" k="position" />
                <Th label="Estado" k="status" />
                <Th label="% Asist." k="attendance_pct" className="text-center" />
                <Th label="Min." k="minutes" className="text-right" />
                <Th label="% Min. equipo" k="minutes_pct_total" className="text-center" />
                <Th label="% Min. conv." k="minutes_pct_called" className="text-center" />
                <Th label="Conv." k="matches_called" className="text-center" />
                <Th label="Goles" k="goals" className="text-center" />
                <Th label="TA/TR" k="yellow" className="text-center" />
                <Th label="R. médico" k="medical_expiry" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="cursor-pointer hover:bg-purple-50" onClick={() => goPlayer(r.id)} data-testid={`squad-row-${r.id}`}>
                  <TableCell className="text-center">{r.dorsal ?? "—"}</TableCell>
                  <TableCell className="font-semibold whitespace-nowrap">{r.name}</TableCell>
                  <TableCell className="text-xs">{r.position || "—"}</TableCell>
                  <TableCell>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${STATUS_STYLE[r.status] || ""}`}>{r.status}</span>
                  </TableCell>
                  <TableCell className="text-center text-xs font-bold">
                    <button className="text-emerald-600 hover:underline" onClick={(e) => goPlayerStat(e, r.id, "detalle-asistencia")} data-testid={`squad-att-${r.id}`}>{r.attendance_pct}%</button>
                  </TableCell>
                  <TableCell className="text-right font-bold">{r.minutes}'</TableCell>
                  <TableCell className="text-center text-xs">
                    <button className={`font-bold hover:underline ${r.played_with_record ? MIN_A.text : "text-slate-400"}`}
                      onClick={(e) => { e.stopPropagation(); setBreakdown({ playerId: r.id, playerName: r.name }); }}
                      data-testid={`squad-min-total-${r.id}`}>
                      {r.played_with_record ? `${String(r.minutes_pct_total).replace(".", ",")}%` : "Sin datos"}
                    </button>
                  </TableCell>
                  <TableCell className="text-center text-xs">
                    <button className={`font-bold hover:underline ${r.matches_called ? MIN_B.text : "text-slate-400"}`}
                      onClick={(e) => { e.stopPropagation(); setBreakdown({ playerId: r.id, playerName: r.name }); }}
                      data-testid={`squad-min-called-${r.id}`}>
                      {r.matches_called ? `${String(r.minutes_pct_called).replace(".", ",")}%` : "Sin datos"}
                    </button>
                  </TableCell>
                  <TableCell className="text-center text-xs">
                    <button className="text-slate-700 hover:underline font-bold" onClick={(e) => goPlayerStat(e, r.id, "detalle-partidos", "called")} data-testid={`squad-conv-${r.id}`}>{r.matches_called}</button>
                  </TableCell>
                  <TableCell className="text-center text-xs">{r.goals || "—"}</TableCell>
                  <TableCell className="text-center text-xs">
                    {r.yellow ? <span className="text-amber-600 font-bold">{r.yellow}</span> : "0"}/{r.red ? <span className="text-rose-600 font-bold">{r.red}</span> : "0"}
                  </TableCell>
                  <TableCell className={`text-xs font-bold whitespace-nowrap ${MED_STYLE[r.medical_level]}`}>
                    {r.medical_expiry ? fmtD(r.medical_expiry) : "Sin registro"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Section>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Asistencia */}
        <Section title="Asistencia (sesiones realizadas)" icon={ClipboardCheck} testId="section-team-attendance">
          <div className="grid grid-cols-3 gap-2 mb-3">
            <Mini label="Sesiones" value={attendance.sessions_done} onClick={() => navigate(`/trainings?team_id=${teamId}`)} testId="att-sessions" />
            <Mini label="% Asistencia" value={`${attendance.attendance_pct}%`} accent="text-emerald-600" testId="att-pct" onClick={() => goTeamStat("attendance")} />
            <Mini label="% Puntualidad" value={`${attendance.punctuality_pct}%`} accent="text-yellow-600" testId="att-punctuality" />
            <Mini label="Retrasos" value={attendance.retraso} accent="text-yellow-600" testId="att-late" />
            <Mini label="Ausencias" value={attendance.no_asiste} accent="text-rose-600" testId="att-absent" />
            <Mini label="Media/sesión" value={attendance.avg_per_session} testId="att-avg" />
          </div>
          {attendance.recent_pct != null && attendance.previous_pct != null && (
            <p className="text-[11px] text-slate-500 mb-2" data-testid="att-comparison">
              Últimas 4 semanas: <b>{attendance.recent_pct}%</b> · 4 anteriores: <b>{attendance.previous_pct}%</b>
              {" "}({attendance.recent_pct >= attendance.previous_pct ? "▲" : "▼"} {Math.abs(attendance.recent_pct - attendance.previous_pct).toFixed(1)} pts)
            </p>
          )}
          {attendance.weekly.length > 1 && (
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={attendance.weekly.map((w) => ({ ...w, week: fmtD(w.week).slice(0, 5) }))}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="week" tick={{ fontSize: 10 }} /><YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Tooltip /><Line type="monotone" dataKey="pct" name="% asistencia" stroke="#7E22CE" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          )}
          <div className="grid grid-cols-2 gap-3 mt-2 text-[11px]">
            <div>
              <p className="font-black text-slate-500 uppercase tracking-wide mb-1">Top asistencia</p>
              {attendance.top.map((r) => (
                <button key={r.id} className="block w-full text-left hover:text-purple-700" onClick={() => goPlayer(r.id)} data-testid={`att-top-${r.id}`}>
                  {r.name} · <b>{r.attendance_pct}%</b>
                </button>
              ))}
            </div>
            <div>
              <p className="font-black text-slate-500 uppercase tracking-wide mb-1">Más retrasos</p>
              {attendance.late_top.length === 0 && <p className="text-slate-400">Sin retrasos.</p>}
              {attendance.late_top.map((r) => (
                <button key={r.id} className="block w-full text-left hover:text-purple-700" onClick={() => goPlayer(r.id)}>
                  {r.name} · <b>{r.retraso}</b>
                </button>
              ))}
            </div>
          </div>
        </Section>

        {/* Entrenamientos */}
        <Section title="Entrenamientos" icon={CalendarDays} testId="section-team-trainings"
          action={<Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate(`/trainings?team_id=${teamId}`)} data-testid="trainings-view-all">Ver entrenamientos</Button>}>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <Mini label="Realizados" value={trainings.done} testId="tr-done" />
            <Mini label="Programados" value={trainings.scheduled} testId="tr-scheduled" />
            <Mini label="Sin asistencia" value={trainings.without_attendance} accent={trainings.without_attendance ? "text-amber-600" : ""} testId="tr-noatt" />
          </div>
          {trainings.last && (
            <p className="text-xs text-slate-600 mb-1">Último: <b>{fmtD(trainings.last.date)}</b>{trainings.last.time ? ` · ${trainings.last.time}` : ""}</p>
          )}
          <p className="font-black text-slate-500 uppercase tracking-wide text-[11px] mt-2 mb-1">Próximos</p>
          {trainings.upcoming.length === 0 && <p className="text-xs text-slate-400">No hay entrenamientos programados.</p>}
          {trainings.upcoming.map((t) => (
            <button key={t.id} className="flex w-full items-center justify-between text-xs py-1.5 border-b last:border-0 hover:text-purple-700"
              onClick={() => navigate(`/trainings/${t.id}`)} data-testid={`upcoming-training-${t.id}`}>
              <span>{fmtD(t.date)}{t.time ? ` · ${t.time}` : ""}</span>
              <span className="text-slate-400 truncate max-w-[50%]">{t.notes || ""}</span>
            </button>
          ))}
          {canEdit && (
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate(`/trainings?team_id=${teamId}`)} data-testid="tr-register-att">Registrar asistencia</Button>
              <Button size="sm" className="h-7 text-xs bg-purple-600 hover:bg-purple-700 text-white" onClick={() => navigate(`/trainings?team_id=${teamId}`)} data-testid="tr-create">Crear entrenamiento</Button>
            </div>
          )}
        </Section>

        {/* Resultados */}
        <Section title="Partidos y resultados" icon={Trophy} testId="section-team-results">
          <div className="grid grid-cols-4 gap-2 mb-3">
            <Mini label="Jugados" value={results.pj} onClick={() => navigate(`/matches?team_id=${teamId}`)} testId="res-pj" />
            <Mini label="V / E / D" value={`${results.v}·${results.e}·${results.d}`} testId="res-ved" />
            <Mini label="Goles" value={`${results.gf}-${results.gc}`} testId="res-goals" />
            <Mini label="% Victorias" value={`${results.win_pct}%`} accent="text-emerald-600" testId="res-winpct" />
            <Mini label="Dif. goles" value={results.diff > 0 ? `+${results.diff}` : results.diff} testId="res-diff" />
            <Mini label="Porterías a 0" value={results.clean} testId="res-clean" />
            <Mini label="Racha" value={results.streak || "—"} testId="res-streak" />
            {results.standing && <Mini label={`Posición (J${results.standing_jornada})`} value={`${results.standing.posicion}º`} accent="text-purple-700" testId="res-standing" />}
          </div>
          <p className="font-black text-slate-500 uppercase tracking-wide text-[11px] mb-1">Últimos resultados</p>
          {results.last5.length === 0 && <p className="text-xs text-slate-400">Sin partidos finalizados.</p>}
          {results.last5.map((m) => (
            <button key={m.id} className="flex w-full items-center gap-2 text-xs py-1.5 border-b last:border-0 hover:bg-purple-50 rounded px-1"
              onClick={() => navigate(`/matches/${m.id}`)} data-testid={`last-match-${m.id}`}>
              <span className={`w-5 h-5 rounded-full grid place-items-center text-white font-black text-[10px] ${OUT_STYLE[m.outcome]}`} title={{ V: "Victoria", E: "Empate", D: "Derrota" }[m.outcome]}>
                {m.outcome}
              </span>
              <span className="text-slate-500">{fmtD(m.date)}{m.jornada ? ` · J${m.jornada}` : ""}</span>
              <span className="font-semibold flex-1 text-left truncate">{m.rival}</span>
              <span className="text-slate-400">{m.home_away === "casa" ? "Casa" : "Fuera"}</span>
              <span className="font-black">{m.result}</span>
            </button>
          ))}
        </Section>

        {/* Próximos partidos */}
        <Section title="Próximos partidos" icon={Clock} testId="section-team-upcoming">
          {upcoming_matches.length === 0 && <p className="text-xs text-slate-400" data-testid="no-upcoming">No hay próximos partidos programados.</p>}
          {upcoming_matches.map((m, i) => (
            <div key={m.id} className={`rounded-lg border p-2.5 mb-2 ${i === 0 ? "border-purple-300 bg-purple-50" : "border-slate-200"}`} data-testid={`upcoming-match-${m.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-1">
                <div className="text-xs">
                  <b>{fmtD(m.date)}</b>{m.kickoff_time ? ` · ${m.kickoff_time}` : ""}{m.jornada ? ` · J${m.jornada}` : ""}
                  <span className="ml-1 font-semibold">{m.home_away === "casa" ? "vs" : "@"} {m.rival}</span>
                </div>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${m.convocation === "creada" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  Convocatoria {m.convocation}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {m.campo && <span className="text-[11px] text-slate-500 inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{m.campo}</span>}
                {m.map_url && <a href={m.map_url} target="_blank" rel="noreferrer" className="text-[11px] text-purple-700 font-bold" onClick={(e) => e.stopPropagation()}>Ver ubicación</a>}
                <Button size="sm" variant="outline" className="h-6 text-[10px] ml-auto" onClick={() => navigate(`/matches/${m.id}`)} data-testid={`upcoming-open-${m.id}`}>
                  {m.convocation === "creada" ? "Editar convocatoria" : "Crear convocatoria"}
                </Button>
              </div>
            </div>
          ))}
        </Section>
      </div>

      {/* Clasificación de liga (oficial RFFM, independiente de los filtros) */}
      <LeagueStandings teamId={teamId} canEdit={canEdit} isPreseason={scopeToPhase(scope) === "PRESEASON"} />

      {/* Estadísticas deportivas */}
      <Section title="Estadísticas deportivas" icon={Trophy} testId="section-team-sport">
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
          <Mini label="Min. repartidos" value={sport.total_minutes} testId="sport-minutes" />
          <Mini label="Min. posibles" value={sport.possible_minutes} testId="sport-possible" />
          <Mini label="Media min/jug." value={sport.avg_minutes} testId="sport-avg" />
          <Mini label="% Participación" value={`${sport.avg_participation_pct}%`} testId="sport-participation" />
          <Mini label="Titularidades" value={sport.titulares} testId="sport-starters" />
          <Mini label="Convocatorias" value={sport.convocatorias} testId="sport-called" />
        </div>
        {sport.distribution.some((d) => d.minutos > 0) && (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={sport.distribution.slice(0, 18)}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-30} height={45} textAnchor="end" />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip /><Bar dataKey="minutos" fill="#9333EA" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2 text-[11px]">
          <div>
            <p className="font-black text-slate-500 uppercase tracking-wide mb-1">Goleadores</p>
            {sport.scorers.length === 0 && <p className="text-slate-400">Sin goles registrados.</p>}
            {sport.scorers.map((r) => (
              <button key={r.id} className="block hover:text-purple-700" onClick={() => goPlayer(r.id)}>{r.name} · <b>{r.goals}</b></button>
            ))}
          </div>
          <div>
            <p className="font-black text-slate-500 uppercase tracking-wide mb-1">Más minutos</p>
            {sport.top_minutes.map((r) => (
              <button key={r.id} className="block hover:text-purple-700" onClick={() => goPlayer(r.id)}>{r.name} · <b>{r.minutes}'</b></button>
            ))}
          </div>
          <div>
            <p className="font-black text-slate-500 uppercase tracking-wide mb-1">Tarjetas</p>
            {sport.carded.length === 0 && <p className="text-slate-400">Sin tarjetas.</p>}
            {sport.carded.map((r) => (
              <button key={r.id} className="block hover:text-purple-700" onClick={() => goPlayer(r.id)}>
                {r.name} · <b className="text-amber-600">{r.yellow}</b>/<b className="text-rose-600">{r.red}</b>
              </button>
            ))}
          </div>
        </div>
      </Section>

      <p className="text-[11px] text-slate-400 text-right" data-testid="team-dashboard-updated">
        Última actualización: {new Date(data.generated_at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}
      </p>

      <MinutesBreakdownDialog
        open={!!breakdown}
        onClose={() => setBreakdown(null)}
        playerId={breakdown?.playerId}
        playerName={breakdown?.playerName}
        season={`${season}-${String(season + 1).slice(2)}`}
        scope={scope}
        dateFrom={`${season}-07-01`}
        dateTo={`${season + 1}-06-30`}
      />

      <AlertDetailDialog
        alert={alertDetail}
        onClose={() => setAlertDetail(null)}
        teamId={teamId}
        season={season}
        navigate={navigate}
        reload={reload}
      />
    </div>
  );
}
